/**
 * Kalibráció — the calibration grouping of handover rev7 §11.10.
 *
 * "Responses grouped by occasion × serving window × guest profile × quantity
 *  level. A cell is reviewed when it holds `calib_min_n` (10) responses and
 *  either end of the scale exceeds `calib_flag_pct` (30%). The review is
 *  Balázs's decision; nothing adjusts itself. Response rate is shown beside
 *  every verdict, and cells under 30% response are labelled."
 *
 * Three things to read on this screen and nothing more:
 *
 *  - **Nothing adjusts itself.** There is no button here. The screen lists the
 *    cells and marks the ones worth looking at; what happens next is a decision
 *    at a calibration review, not a write.
 *  - **The fourth dimension is the savoury/sweet balance.** The quantity level
 *    is retired (Barna, 4 October) and the balance took its place on the same
 *    two columns.
 *  - **The response rate is beside every verdict**, and a cell under 30%
 *    response is labelled, because a verdict from two people is not a verdict.
 */

import { useState } from 'react'

import { fmt, Loading, useConsoleData } from '../common.tsx'

interface Cell {
  occasion: string | null
  occasion_label: string | null
  serving_window: string | null
  guest_profile: string | null
  balance_sav: string
  balance_sw: string
  sent: number
  responses: number
  n: number
  verdicts: Record<string, number>
  guests_came_avg: number | null
  would_order_again: number
  response_rate: number | null
  low_response: boolean | null
  too_much_pct: number | null
  too_little_pct: number | null
  review: boolean
}

interface Scale {
  key: string
  label: string
}

const PROFILE_HU: Record<string, string> = {
  light: 'kis étkű',
  average: 'átlagos étkű',
  hearty: 'nagy étkű',
}

const BALANCE_HU: Record<string, string> = {
  less: 'kevesebb',
  suggested: 'javasolt',
  more: 'több',
}

/** §5.12: one decimal, with a Hungarian decimal comma. A share rounded to a
 *  whole number would read 30% on both sides of calib_flag_pct, which is the
 *  one place the difference decides something. Closed up, by Barna's ruling 7
 *  of 5 October 2026 (`build/FIXES.md` item 43). */
const pct = (value: number | null): string =>
  value === null ? '—' : `${fmt.n(value, 1)}%`

export function Calibration(): JSX.Element {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const { data, error, loading } = useConsoleData<{
    cells: Cell[]
    scale: Scale[]
    from: string
    to: string
    params: { calib_min_n: number; calib_flag_pct: number; calib_low_response_pct: number }
  }>('calibration', { from: from || undefined, to: to || undefined })

  return (
    <section data-testid="view-calibration">
      <h2>Kalibráció</h2>
      <p className="lead">
        §11.10: a kérdőív válaszai alkalom × kiszolgálási ablak × vendégprofil × sós/édes arány
        szerint. Egy cella akkor kerül felülvizsgálatra, ha legalább{' '}
        {data === null ? '…' : fmt.n(data.params.calib_min_n)} válasza van, és a skála valamelyik
        széle meghaladja a {data === null ? '…' : pct(data.params.calib_flag_pct)}-ot. A
        döntés Balázsé; itt semmi nem állít magán.
      </p>

      <label htmlFor="calib-from">Rendezvény dátuma, -tól</label>
      <input
        id="calib-from"
        type="date"
        value={from}
        onChange={(event) => setFrom(event.target.value)}
        data-testid="calib-from"
      />
      <label htmlFor="calib-to">-ig</label>
      <input
        id="calib-to"
        type="date"
        value={to}
        onChange={(event) => setTo(event.target.value)}
        data-testid="calib-to"
      />

      <Loading loading={loading} error={error} />

      {data !== null && data.cells.length === 0 && (
        <p className="note" data-testid="calibration-empty">
          Ebben az időszakban nincs kiküldött kérdőív.
        </p>
      )}

      {data !== null && data.cells.length > 0 && (
        <table data-testid="calibration-table">
          <thead>
            <tr>
              <th>Alkalom</th>
              <th>Ablak</th>
              <th>Profil</th>
              <th>Arány (sós · édes)</th>
              <th className="num">Kiküldve</th>
              <th className="num">Válasz</th>
              <th className="num">Válaszadási arány</th>
              {data.scale.map((step) => <th className="num" key={step.key}>{step.label}</th>)}
              <th className="num">sok maradt %</th>
              <th className="num">kevés volt %</th>
              <th>Felülvizsgálat</th>
            </tr>
          </thead>
          <tbody>
            {data.cells.map((cell) => {
              const key = [
                cell.occasion, cell.serving_window, cell.guest_profile,
                cell.balance_sav, cell.balance_sw,
              ].join('·')
              return (
                <tr key={key} data-testid={`calib-${key}`}>
                  <td>{cell.occasion_label ?? cell.occasion ?? '—'}</td>
                  <td>{cell.serving_window ?? '—'}</td>
                  <td>{PROFILE_HU[cell.guest_profile ?? ''] ?? cell.guest_profile ?? '—'}</td>
                  <td>
                    {BALANCE_HU[cell.balance_sav] ?? cell.balance_sav}
                    {' · '}
                    {BALANCE_HU[cell.balance_sw] ?? cell.balance_sw}
                  </td>
                  <td className="num">{fmt.n(cell.sent)}</td>
                  <td className="num">{fmt.n(cell.responses)}</td>
                  <td className="num">
                    {pct(cell.response_rate)}
                    {cell.low_response === true && (
                      <span className="tag warn" data-testid={`low-response-${key}`}>
                        kevés válasz
                      </span>
                    )}
                  </td>
                  {data.scale.map((step) => (
                    <td className="num" key={step.key}>{fmt.n(cell.verdicts[step.key] ?? 0)}</td>
                  ))}
                  <td className="num">{pct(cell.too_much_pct)}</td>
                  <td className="num">{pct(cell.too_little_pct)}</td>
                  <td>
                    {cell.review
                      ? <span className="tag stop" data-testid={`review-${key}`}>felülvizsgálandó</span>
                      : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <p className="note">
        §11.10: a felülvizsgálat Balázs döntése. A kalibráció semmit nem módosít automatikusan, és
        a mennyiségi paramétereket továbbra is a §12 paraméter-regiszter tartja.
      </p>
    </section>
  )
}
